import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import type { ElementRef, QueryList, TrackByFunction } from '@angular/core';
import { Component, Injector, ViewChildren, inject, ChangeDetectionStrategy } from '@angular/core';

import { trackByProp } from '@core/common-legacy';
import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  DropdownItemComponent,
  HeadingComponent,
  DropdownHeaderComponent,
  DropdownComponent,
} from '@core/user-interface-legacy';

import { WysiwygAction, WysiwygEmbeddableComponent } from '../../../adapter/wysiwyg-adapter.types';
import type { Placeholder } from '../../placeholder';
import type { WysiwygPlaceholderAction } from '../wysiwyg-placeholder.types';
import { ActionType } from '../wysiwyg-placeholder.types';

export interface WysiwygPlaceholderState {
  placeholders: Placeholder[];
}

@Component({
  selector: 'rt-wysiwyg-placeholder-typeahead',
  templateUrl: './wysiwyg-placeholder-typeahead.component.html',
  styleUrls: ['./wysiwyg-placeholder-typeahead.component.less'],
  host: {
    '(document:keyup)': 'handleKeyboardEvent($event)',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    HeadingComponent,
    TranslatePipe,
  ],
})
export class WysiwygPlaceholderTypeaheadComponent extends WysiwygEmbeddableComponent<
  WysiwygPlaceholderState,
  WysiwygPlaceholderAction
> {
  protected readonly injector: Injector;

  public state: WysiwygPlaceholderState;
  public selectedPlaceholderIndex: number = 0;

  public trackByPlaceholderName: TrackByFunction<Placeholder> = trackByProp<Placeholder>({
    prop: 'id',
  });

  @ViewChildren('placeholdersList') private placeholdersList: QueryList<ElementRef>;
  @ViewChildren('item') private item: QueryList<DropdownItemComponent>;

  constructor() {
    const injector = inject(Injector);

    super(injector);
    this.injector = injector;

    this.state = {
      placeholders: [],
    };
  }

  public ngAfterViewInit(): void {
    this.cdRef.detectChanges();
  }

  public insertPlaceholder(placeholder: Placeholder) {
    this.emit(new WysiwygAction(ActionType.PlaceholderInsertFromDropdown, placeholder));
  }
  public removeDropdown() {
    this.emit(new WysiwygAction(ActionType.PlaceholderDropdownDelete, {}));
  }

  public handleKeyboardEvent(event: KeyboardEvent): void {
    switch (<KeyboardEventCode>event.code) {
      case KeyboardEventCode.Enter: {
        this.insertPlaceholder(this.state.placeholders[this.selectedPlaceholderIndex]);
        break;
      }

      case KeyboardEventCode.ArrowUp: {
        if (this.selectedPlaceholderIndex > 0) {
          this.selectedPlaceholderIndex = this.selectedPlaceholderIndex - 1;
          this.setScrollPosition(this.selectedPlaceholderIndex, false);
        }

        break;
      }

      case KeyboardEventCode.ArrowDown: {
        if (this.selectedPlaceholderIndex < this.state.placeholders.length - 1) {
          this.selectedPlaceholderIndex = this.selectedPlaceholderIndex + 1;
          this.setScrollPosition(this.selectedPlaceholderIndex, true);
        }
        break;
      }
      default:
        return;
    }
    this.cdRef.detectChanges();
  }

  public withoutBrackets(str: string): string {
    return str.slice(1, -1);
  }

  protected onStateChange(
    stateUpdate: Partial<WysiwygPlaceholderState>,
    prevState: WysiwygPlaceholderState,
  ): void {
    const nextState = {
      ...prevState,
      ...stateUpdate,
    };
    this.state = {
      ...this.state,
      placeholders: nextState.placeholders,
    };

    this.cdRef.detectChanges();
  }

  private setScrollPosition(index: number, increment: boolean): void {
    const containerHeight = this.placeholdersList.first.nativeElement.offsetHeight;
    const itemOffset = this.item?.get(this.selectedPlaceholderIndex)?.elRef.nativeElement.offsetTop;
    const itemHeight = this.item?.get(0)?.elRef.nativeElement.offsetHeight;
    const itemsCount = this.item?.length;
    const listHeight = itemsCount * itemHeight;
    let scrollTop = this.placeholdersList.first.nativeElement.scrollTop;

    if (
      (itemOffset > containerHeight && increment) ||
      (itemOffset > 0 && !increment) ||
      itemOffset > containerHeight + listHeight
    ) {
      scrollTop = index * itemHeight;
    }

    if (index === 0) scrollTop = 0;

    this.placeholdersList.first.nativeElement.scrollTop = scrollTop;
  }
}
